import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../config/api';

interface EmailIntelligenceManagerProps {
  user: any;
}

export const EmailIntelligenceManager: React.FC<EmailIntelligenceManagerProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<string>('templates');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('weekly');
  const [showAIReview, setShowAIReview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editedTemplates, setEditedTemplates] = useState<{[key: string]: any}>({});

  // Fetch AI insights for email content generation
  const { data: insights } = useQuery({
    queryKey: ['ai-insights', user?.id],
    queryFn: () => apiClient.getAIInsights(user?.id, '7d'),
    enabled: !!user?.id,
  });

  // Fetch micronutrient data for comprehensive analysis
  const { data: micronutrientData } = useQuery({
    queryKey: ['micronutrients', user?.id],
    queryFn: () => apiClient.getMicronutrients(user?.id, '7d'),
    enabled: !!user?.id,
  });

  // Track sending state
  const [isSending, setIsSending] = useState(false);

  const tabs = [
    { id: 'templates', label: 'Email Templates', icon: '📧' },
    { id: 'preview', label: 'Preview & Send', icon: '👁️' },
    { id: 'scheduling', label: 'Automation', icon: '⏰' },
    { id: 'analytics', label: 'Analytics', icon: '📊' }
  ];

  const emailTemplates = [
    {
      id: 'weekly',
      name: 'Weekly Nutrition Summary',
      type: 'weekly',
      subject: 'Your Weekly Nutrition Insights',
      enabled: true,
      schedule: 'Sundays at 9:00 AM'
    },
    {
      id: 'daily',
      name: 'Daily Check-in',
      type: 'daily',
      subject: 'Today\'s Nutrition Focus',
      enabled: false,
      schedule: 'Daily at 8:00 AM'
    },
    {
      id: 'red-flag',
      name: 'Red Flag Alert',
      type: 'alert',
      subject: 'Urgent: Nutrition Concern Detected',
      enabled: true,
      schedule: 'Immediate when triggered'
    }
  ];

  const generateEmailPreview = (templateType: string = selectedTemplate) => {
    const clientName = user?.fullName || user?.first_name || 'there';
    const editedTemplate = editedTemplates[templateType];
    
    if (editedTemplate) {
      // Use edited template if available
      return {
        subject: editedTemplate.subject || `${clientName}, your nutrition insights are ready! 📊`,
        content: editedTemplate.content || generateWeeklyContent(clientName)
      };
    }
    
    // Use default templates
    switch (templateType) {
      case 'weekly':
        return {
          subject: `${clientName}, your weekly nutrition summary is ready! 📊`,
          content: generateWeeklyContent(clientName)
        };
      case 'daily':
        return {
          subject: `Good morning ${clientName}! Today's nutrition focus 🌟`,
          content: generateDailyContent(clientName)
        };
      case 'red-flag':
        return {
          subject: `${clientName}, let's address a nutrition concern 🚨`,
          content: generateRedFlagContent(clientName)
        };
      default:
        return {
          subject: `${clientName}, your nutrition insights are ready! 📊`,
          content: generateWeeklyContent(clientName)
        };
    }
  };

  const generateWeeklyContent = (clientName: string) => {
    // Use actual nutrition data for automated weekly overview
    const data = micronutrientData as any;
    
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Hi ${clientName}! 👋</h2>
        <p>Here's your weekly nutrition overview based on your logged meals:</p>
        
        <h3 style="color: #059669;">Key Nutrients Status:</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="background-color: #f9fafb;">
            <td style="padding: 8px; border: 1px solid #e5e7eb;">Protein</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${data?.protein || 0}% of RDA</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${(data?.protein || 0) >= 80 ? '✅' : '⚠️'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">Vitamin D</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${data?.vitamin_d || 0}% of RDA</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${(data?.vitamin_d || 0) >= 80 ? '✅' : '⚠️'}</td>
          </tr>
          <tr style="background-color: #f9fafb;">
            <td style="padding: 8px; border: 1px solid #e5e7eb;">Iron</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${data?.iron || 0}% of RDA</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${(data?.iron || 0) >= 80 ? '✅' : '⚠️'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">Vitamin B12</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${data?.vitamin_b12 || 0}% of RDA</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${(data?.vitamin_b12 || 0) >= 80 ? '✅' : '⚠️'}</td>
          </tr>
        </table>
        
        <p style="color: #6b7280; font-style: italic; margin-top: 20px;">
          This automated summary shows your key nutrient levels. For detailed insights and recommendations, 
          I'll send a personalized analysis separately.
        </p>
      </div>
    `;
  };

  const generateDailyContent = (clientName: string) => {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Good morning ${clientName}! ☀️</h2>
        
        <h3 style="color: #059669;">Today's Focus:</h3>
        <div style="background-color: #ecfdf5; padding: 15px; border-radius: 8px; margin: 15px 0;">
          <h4 style="margin: 0; color: #065f46;">💧 Hydration Goal</h4>
          <p style="margin: 5px 0;">Aim for 8 glasses of water throughout the day</p>
        </div>
        
        <div style="background-color: #eff6ff; padding: 15px; border-radius: 8px; margin: 15px 0;">
          <h4 style="margin: 0; color: #1d4ed8;">🥬 Vegetable Challenge</h4>
          <p style="margin: 5px 0;">Include 2 different colored vegetables in your meals today</p>
        </div>
        
        <div style="background-color: #fef3c7; padding: 15px; border-radius: 8px; margin: 15px 0;">
          <h4 style="margin: 0; color: #92400e;">🥜 Protein Power</h4>
          <p style="margin: 5px 0;">Add a handful of nuts or seeds to your snack</p>
        </div>
        
        <p style="color: #6b7280; font-style: italic;">
          Small daily choices add up to big nutrition wins! You've got this! 💪
        </p>
      </div>
    `;
  };

  const generateRedFlagContent = (clientName: string) => {
    const data = micronutrientData as any;
    const lowNutrients = [];
    
    if ((data?.vitamin_d || 0) < 25) lowNutrients.push('Vitamin D');
    if ((data?.iron || 0) < 25) lowNutrients.push('Iron');
    if ((data?.vitamin_b12 || 0) < 25) lowNutrients.push('Vitamin B12');
    
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #dc2626;">Important: Nutrition Alert for ${clientName} 🚨</h2>
        
        <div style="background-color: #fef2f2; border-left: 4px solid #dc2626; padding: 15px; margin: 15px 0;">
          <h3 style="color: #dc2626; margin-top: 0;">Nutrients Requiring Attention:</h3>
          ${lowNutrients.length > 0 ? 
            `<ul>${lowNutrients.map(nutrient => `<li><strong>${nutrient}</strong> - Below 25% of RDA</li>`).join('')}</ul>` :
            '<p>Great news! No critical deficiencies detected this week.</p>'
          }
        </div>
        
        <h3 style="color: #059669;">Immediate Action Steps:</h3>
        <ol>
          <li><strong>Schedule a consultation</strong> - Let's discuss these levels in detail</li>
          <li><strong>Targeted supplementation</strong> - I'll recommend specific options</li>
          <li><strong>Food-first approach</strong> - We'll identify nutrient-dense alternatives</li>
        </ol>
        
        <p style="color: #374151; background-color: #f3f4f6; padding: 12px; border-radius: 8px;">
          <strong>Note:</strong> This alert is generated automatically when nutrient levels drop below 25% of RDA. 
          Please don't worry - these issues are very manageable with the right approach!
        </p>
        
        <p style="color: #6b7280; font-style: italic;">
          I'm here to support you. Let's schedule a call to address these concerns together.
        </p>
      </div>
    `;
  };

  const handleSendEmail = async () => {
    // For AI-generated content (not weekly automated), require review
    if (selectedTemplate !== 'weekly') {
      setShowAIReview(true);
      return;
    }
    
    // For weekly automated (levels only), send directly
    await sendEmailDirectly();
  };

  const sendEmailDirectly = async () => {
    const preview = generateEmailPreview();
    setIsSending(true);
    
    try {
      // Use the actual backend email endpoint structure
      const emailData = {
        recipientEmail: user?.email,
        recipientName: user?.fullName || user?.first_name || user?.email,
        customSubject: preview.subject,
        attachPDF: false, // Disable PDF for now since we're sending custom content
        // Pass our custom HTML content
        customContent: preview.content
      };

      // Get CSRF token first
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.stepsofcornwall.co.uk';
      const csrfResponse = await fetch(`${API_BASE_URL}/api/csrf-token`);
      const csrfData = await csrfResponse.json();
      
      // Use the API client's configured base URL
      const response = await fetch(`${API_BASE_URL}/api/email/weekly-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-CSRF-Token': csrfData.csrf_token
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      await response.json();
      alert('Email sent successfully!');
      setShowAIReview(false);
    } catch (error) {
      console.error('Failed to send email:', error);
      alert(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSending(false);
    }
  };


  const getTemplateToEdit = (templateId: string) => {
    const originalTemplate = emailTemplates.find(t => t.id === templateId);
    const editedTemplate = editedTemplates[templateId];
    return editedTemplate || originalTemplate;
  };

  const updateEditedTemplate = (templateId: string, field: string, value: any) => {
    setEditedTemplates(prev => ({
      ...prev,
      [templateId]: {
        ...getTemplateToEdit(templateId),
        [field]: value
      }
    }));
  };

  const saveTemplateChanges = (templateId: string) => {
    // In a real app, this would save to the backend
    alert('Template changes saved! (This would sync to backend in production)');
    setEditingTemplate(null);
  };

  const discardTemplateChanges = (templateId: string) => {
    const { [templateId]: removed, ...remaining } = editedTemplates;
    setEditedTemplates(remaining);
    setEditingTemplate(null);
  };

  const getTemplateContent = (templateType: string) => {
    const editedTemplate = editedTemplates[templateType];
    if (editedTemplate?.content) {
      return editedTemplate.content;
    }
    
    const clientName = user?.fullName || user?.first_name || 'there';
    
    switch (templateType) {
      case 'weekly':
        return generateWeeklyContent(clientName);
      case 'daily':
        return generateDailyContent(clientName);
      case 'red-flag':
        return generateRedFlagContent(clientName);
      default:
        return generateWeeklyContent(clientName);
    }
  };

  const renderTemplateEditor = () => {
    if (!editingTemplate) return null;
    
    const template = getTemplateToEdit(editingTemplate);
    const templateContent = getTemplateContent(editingTemplate);
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">
                Edit Template: {template?.name}
              </h3>
              <button 
                onClick={() => discardTemplateChanges(editingTemplate)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Template Settings */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={template?.name || ''}
                    onChange={(e) => updateEditedTemplate(editingTemplate, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={template?.subject || ''}
                    onChange={(e) => updateEditedTemplate(editingTemplate, 'subject', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Your Weekly Nutrition Insights"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule
                  </label>
                  <input
                    type="text"
                    value={template?.schedule || ''}
                    onChange={(e) => updateEditedTemplate(editingTemplate, 'schedule', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Sundays at 9:00 AM"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Content
                  </label>
                  <div className="text-xs text-gray-500 mb-2">
                    Available variables: {'{clientName}'}, {'{data.protein}'}, {'{data.vitamin_d}'}, {'{data.iron}'}, {'{data.vitamin_b12}'}
                  </div>
                  <textarea
                    rows={12}
                    value={editedTemplates[editingTemplate]?.content || templateContent}
                    onChange={(e) => updateEditedTemplate(editingTemplate, 'content', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                    placeholder="Enter your email template HTML content here..."
                  />
                </div>
              </div>

              {/* Live Preview */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Live Preview</h4>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <div className="text-sm text-gray-600">Subject:</div>
                    <div className="font-medium">{template?.subject || 'Email Subject'}</div>
                  </div>
                  <div className="p-4 bg-white max-h-96 overflow-y-auto">
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: editedTemplates[editingTemplate]?.content || templateContent 
                      }}
                    />
                  </div>
                </div>
                
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h5 className="font-medium text-blue-800 mb-2">📝 Template Tips</h5>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Use {'{clientName}'} for personalization</li>
                    <li>• Use {'{data.nutrient_name}'} for nutrition data</li>
                    <li>• HTML tags are supported for formatting</li>
                    <li>• Keep mobile-friendly with max-width: 600px</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4">
            <div className="flex space-x-3">
              <button 
                onClick={() => saveTemplateChanges(editingTemplate)}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                Save Changes
              </button>
              <button 
                onClick={() => discardTemplateChanges(editingTemplate)}
                className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setSelectedTemplate(editingTemplate);
                  setActiveTab('preview');
                  setEditingTemplate(null);
                }}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Preview
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <h1 className="text-2xl font-bold text-white">
            Email Intelligence Manager 🧠
          </h1>
          <p className="mt-2 text-purple-100">
            AI-powered email insights for {user?.fullName || user?.email}
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {emailTemplates.map((template) => (
            <div key={template.id} className="card">
              <div className="card-header">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {editedTemplates[template.id]?.name || template.name}
                      </h3>
                      {editedTemplates[template.id] && (
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                          Modified
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {editedTemplates[template.id]?.subject || template.subject}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        defaultChecked={template.enabled}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">
                    <strong>Schedule:</strong> {editedTemplates[template.id]?.schedule || template.schedule}
                  </div>
                  <div className="text-sm text-gray-600">
                    <strong>Type:</strong> {template.type}
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setActiveTab('preview');
                      }}
                      className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-md text-sm hover:bg-purple-700"
                    >
                      Preview
                    </button>
                    <button 
                      onClick={() => setEditingTemplate(template.id)}
                      className="flex-1 border border-gray-300 px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Select Template</h3>
              </div>
              <div className="card-body">
                <select 
                  value={selectedTemplate} 
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 mb-3"
                >
                  {emailTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <div className="text-sm text-gray-600 mb-3">
                  {insights ? "✅ AI insights available" : ""}
                  {micronutrientData ? " • ✅ Nutrition data ready" : ""}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Email Preview</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                    <div className="p-2 bg-gray-50 rounded border text-sm">
                      {generateEmailPreview().subject}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content:</label>
                    <div 
                      className="p-4 bg-gray-50 rounded border max-h-64 overflow-y-auto text-sm"
                      dangerouslySetInnerHTML={{ __html: generateEmailPreview().content }}
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button 
                      onClick={handleSendEmail}
                      disabled={isSending}
                      className={`flex-1 px-4 py-2 rounded-md disabled:opacity-50 ${
                        selectedTemplate === 'weekly' 
                          ? 'bg-green-600 text-white hover:bg-green-700' 
                          : 'bg-amber-600 text-white hover:bg-amber-700'
                      }`}
                    >
                      {isSending ? 'Sending...' : 
                       selectedTemplate === 'weekly' ? 'Send Automated' : 'Review & Send'}
                    </button>
                    <button className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50">
                      Schedule
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Review Modal */}
          {showAIReview && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-medium text-gray-900">AI Content Review Required</h3>
                  <button 
                    onClick={() => setShowAIReview(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-amber-800 mb-2">🤖 AI-Generated Content Review</h4>
                  <p className="text-sm text-amber-700 mb-3">
                    This email contains AI-generated insights and recommendations. Please review before sending to ensure accuracy and appropriateness.
                  </p>
                  <div className="text-sm text-amber-700">
                    <strong>Template:</strong> {emailTemplates.find(t => t.id === selectedTemplate)?.name}<br/>
                    <strong>Recipient:</strong> {user?.email}<br/>
                    <strong>Content Type:</strong> AI insights and personalized recommendations
                  </div>
                </div>

                <div className="border rounded-lg p-4 mb-4 max-h-48 overflow-y-auto">
                  <h5 className="font-medium mb-2">Email Preview:</h5>
                  <div 
                    className="text-sm"
                    dangerouslySetInnerHTML={{ __html: generateEmailPreview().content }}
                  />
                </div>

                <div className="flex space-x-3">
                  <button 
                    onClick={sendEmailDirectly}
                    disabled={isSending}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSending ? 'Sending...' : 'Approve & Send'}
                  </button>
                  <button 
                    onClick={() => setShowAIReview(false)}
                    className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => {
                      setShowAIReview(false);
                      setEditingTemplate(selectedTemplate);
                    }}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Edit First
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scheduling Tab */}
      {activeTab === 'scheduling' && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Email Automation</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-8 text-gray-500">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <span className="text-2xl">⚙️</span>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">Automation Coming Soon</h4>
              <p className="text-sm">
                Automated email scheduling and delivery management will be available in the next update.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Email Analytics</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-8 text-gray-500">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <span className="text-2xl">📈</span>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">Analytics Dashboard</h4>
              <p className="text-sm">
                Email delivery rates, open rates, and engagement metrics will be displayed here.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {renderTemplateEditor()}
    </div>
  );
};